import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';

import { useIntl } from 'react-intl-next';
import { mergeRefs } from 'use-callback-ref';

import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import Lozenge from '@atlaskit/lozenge';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { useResponderNames } from '@atlassian/eoc-responder-names';
import { RuleCard } from '@atlassian/eoc-rule-card';

import commonMessages from '../../../common/messages';
import { Rules } from '../../../common/ui/rules';
import { isResponderDeleted } from '../../../common/utils';

import messages from './messages';
import { Container } from './styled';
import { type EscalationRulesProps, type EscalationRulesRefProps } from './types';

// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
const styles = xcss({ textTransform: 'uppercase' });
export const EscalationRules = forwardRef<EscalationRulesRefProps, EscalationRulesProps>(
	(
		{
			entity,
			shouldShowSpinner,
			onEdit,
			onClone,
			onStateChange,
			onDelete,
			onUpdateCollapseStatus,
			hasEditRight,
			hasDeleteRight,
		},
		ref,
	) => {
		const { formatMessage } = useIntl();
		const [isCollapsed, setIsCollapsed] = useState(entity.collapsed);
		const buttonRef = useRef<HTMLButtonElement>(null);

		useImperativeHandle(ref, () => ({
			getActionButtonRef: () => buttonRef,
		}));

		const responders = useResponderNames(
			entity.rules.map((rule) => ({ id: rule.notifyId, type: rule.type })),
		);

		const hasDeletedResponder = responders.some((responder) => isResponderDeleted(responder));

		const toggleCollapsed = () => {
			setIsCollapsed(!isCollapsed);
			onUpdateCollapseStatus();
		};

		return (
			<RuleCard
				value={entity.id}
				droppableId="escalation-policies"
				title={entity.name}
				headingAs="h3"
				actions={
					<Flex alignItems="center" gap="space.150">
						{shouldShowSpinner && (
							<Spinner
								testId="escalation-policies.escalation-policy-actions.deleting"
								size="small"
							/>
						)}
						{hasDeletedResponder && (
							<Lozenge appearance="removed">{formatMessage(messages.missingResponder)}</Lozenge>
						)}
						{!entity.enabled && (
							<Lozenge appearance="moved">
								<Box xcss={styles}>{formatMessage(commonMessages.disabled)}</Box>
							</Lozenge>
						)}
						<DropdownMenu<HTMLButtonElement>
							trigger={({ triggerRef, ...props }) => (
								<IconButton
									label={formatMessage(commonMessages.showMore, {
										name: entity.name,
									})}
									{...props}
									ref={mergeRefs([triggerRef, buttonRef])}
									appearance="subtle"
									icon={MoreIcon}
								/>
							)}
							shouldRenderToParent
						>
							{(hasEditRight || hasDeleteRight) && (
								<DropdownItemGroup>
									{hasEditRight && (
										<DropdownItem onClick={onEdit}>
											{formatMessage(commonMessages.edit)}
										</DropdownItem>
									)}
									{hasEditRight && (
										<DropdownItem onClick={onClone}>
											{formatMessage(commonMessages.clone)}
										</DropdownItem>
									)}
									{hasEditRight && (
										<DropdownItem onClick={onStateChange}>
											{formatMessage(
												entity.enabled ? commonMessages.disable : commonMessages.enable,
											)}
										</DropdownItem>
									)}
									{hasDeleteRight && (
										<DropdownItem onClick={onDelete}>
											{formatMessage(commonMessages.delete)}
										</DropdownItem>
									)}
								</DropdownItemGroup>
							)}
							<DropdownItemGroup hasSeparator={hasEditRight || hasDeleteRight}>
								<DropdownItem onClick={toggleCollapsed}>
									{isCollapsed ? formatMessage(messages.expand) : formatMessage(messages.collapse)}
								</DropdownItem>
							</DropdownItemGroup>
						</DropdownMenu>
					</Flex>
				}
				gap={`${8 * 1.25}px`}
			>
				{!isCollapsed && (
					<Container>
						<Rules rules={entity.rules} />
					</Container>
				)}
			</RuleCard>
		);
	},
);
